import type {
  DatesSetArg,
  DayHeaderContentArg,
  EventClickArg,
  EventDropArg,
  EventMountArg,
  MoreLinkArg
} from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin, { type DateClickArg } from "@fullcalendar/interaction"
import listPlugin from "@fullcalendar/list"
import FullCalendar from "@fullcalendar/react"
import timeGridPlugin from "@fullcalendar/timegrid"
import { Button, Flex, Typography } from "antd"
import dayjs from "dayjs"
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"

import type {
  CalendarFilters as CalendarFiltersType,
  CalendarModalData
} from "../../redux/calendar.types.ts"
import { type Event } from "../../types/types.ts"
import { parseCalendarFocusDate } from "../../helpers/calendar-deep-link.ts"
import { useWorkingSchedule } from "../../helpers/use-working-schedule.ts"
import {
  scheduleToBreakEvents,
  scheduleToBusinessHours,
  scheduleToVisibleTimeRange,
  scheduleToWorkWeekHiddenDays
} from "../../helpers/working-schedule.ts"
import { eventMatchesSearch } from "../helpers/event-matches-search.ts"
import { resolveNewEventSlot } from "../helpers/new-event-slot.ts"
import { createStartAlignedEventDrag } from "../helpers/start-aligned-event-drag.ts"
import { transformEventsForCalendar } from "../helpers/transform-events-for-calendar"
import { CalendarAvailability } from "./CalendarAvailability/CalendarAvailability.tsx"
import { CalendarEventCell } from "./CalendarEventCell/CalendarEventCell"
import { CalendarFilters } from "./CalendarFilters/CalendarFilters"
import { CalendarHeader } from "./CalendarHeader"
import { CalendarNavigation } from "./CalendarNavigation"
import { CalendarSidebar } from "./CalendarSidebar/CalendarSidebar.tsx"
import "./calendar.less"

const { Text } = Typography

// Static across every render — hoisted so they never give FullCalendar's
// react wrapper a changed prop reference to react to on a re-render that has
// nothing to do with them (e.g. the now-line's own scroll-driven state).
// That reprocessing, multiplied by a state update on every scroll tick, was
// the actual cause of the grid visibly shaking while scrolling — see the
// KB entry for this file's own root-cause note.
const FC_PLUGINS = [
  dayGridPlugin,
  timeGridPlugin,
  interactionPlugin,
  listPlugin
]
const FC_TITLE_FORMAT = {
  weekday: "short" as const,
  month: "short" as const,
  day: "2-digit" as const
}
const FC_DAY_HEADER_FORMAT = {
  weekday: "short" as const,
  day: "numeric" as const
}
const FC_SLOT_LABEL_FORMAT = {
  hour: "numeric" as const,
  minute: "2-digit" as const,
  hour12: true
}
const FC_EVENT_TIME_FORMAT = {
  hour: "numeric" as const,
  minute: "2-digit" as const,
  hour12: true,
  meridiem: "short" as const
}
const FC_NOW_INDICATOR_CONTENT = () => <></>

const FC_VIEWS_STATIC = {
  // `dayMaxEvents` caps the all-day slot as well as the month cell, and the
  // calendar-level value below is the month grid's number — keep the two in
  // step.
  timeGrid: {
    dayMaxEvents: 6
  },
  timeGridDay: {
    titleFormat: { year: "numeric", month: "long", day: "numeric" } // "January 8, 2026"
  },
  listDay: {
    titleFormat: { year: "numeric", month: "long", day: "numeric" }, // "January 8, 2026"
    // Unlike the compact grid cards, the agenda has room for the fuller
    // "11:30 AM" (space, uppercase) FullCalendar gives by default — the
    // grid's own `meridiem: "short"` override is deliberately not applied
    // here.
    eventTimeFormat: { hour: "numeric", minute: "2-digit", hour12: true }
  },
  dayGridMonth: {
    titleFormat: { year: "numeric", month: "long" } // "January 2026"
  },
  timeGridWeek: {
    titleFormat: { year: "numeric", month: "short", day: "numeric" } // "Jan 6, 2026"
  },
  listWeek: {
    titleFormat: { year: "numeric", month: "short", day: "numeric" }, // "Jan 6, 2026"
    eventTimeFormat: { hour: "numeric", minute: "2-digit", hour12: true }
  },
  listMonth: {
    titleFormat: { year: "numeric", month: "short", day: "numeric" }, // "Jan 6, 2026"
    eventTimeFormat: { hour: "numeric", minute: "2-digit", hour12: true }
  }
} as const

interface CalendarProps {
  timezone: string
  accountTimezone?: string | null
  focusDate?: string | null
  filters: CalendarFiltersType
  isMobile: boolean
  events: Event[]
  userId?: string
  //
  createAllowed?: boolean
  editAllowed?: boolean
  viewAllAllowed?: boolean
  //
  onSelectEvent: (id: string) => void
  onSetModalData: (data: CalendarModalData | null) => void
  onFetchEvents: ({ start, end }: Partial<DatesSetArg>) => void
  onFilterChange: (filter: Record<string, unknown>) => void
  onSetMoreInfo: (args: MoreLinkArg) => void
  onDropEvent: (arg: EventDropArg) => void
}

export const Calendar = memo(
  ({
    timezone,
    accountTimezone,
    focusDate,
    filters,
    isMobile,
    userId,
    createAllowed,
    editAllowed,
    viewAllAllowed,
    events: apiEvents = [],
    onSelectEvent,
    onSetModalData,
    onFetchEvents,
    onFilterChange,
    onSetMoreInfo,
    onDropEvent
  }: CalendarProps) => {
    const isFirstRender = useRef(true)
    const calendarRef = useRef<FullCalendar | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const gridWrapRef = useRef<HTMLDivElement | null>(null)
    const startAlignedEventDrag = useMemo(createStartAlignedEventDrag, [])
    const fcPlugins = useMemo(
      () => [...FC_PLUGINS, startAlignedEventDrag.plugin],
      [startAlignedEventDrag]
    )
    const getAPI = () => calendarRef?.current?.getApi()

    const [workingSchedule] = useWorkingSchedule()
    const businessHours = useMemo(
      () => scheduleToBusinessHours(workingSchedule),
      [workingSchedule]
    )
    const workWeekHiddenDays = useMemo(
      () => scheduleToWorkWeekHiddenDays(workingSchedule),
      [workingSchedule]
    )
    // Work Week's own viewport — earliest configured start to latest
    // configured end, computed once from the schedule (never from scroll
    // position or loaded events, see working-schedule.ts). Every other view
    // keeps its own top-level slotMinTime/slotMaxTime (00:00-23:59)
    // untouched.
    const workWeekVisibleRange = useMemo(
      () => scheduleToVisibleTimeRange(workingSchedule),
      [workingSchedule]
    )
    // Concrete dated instances, not a single recurring definition — see
    // scheduleToBreakEvents's own comment for why. Deliberately keyed on
    // [workingSchedule, timezone] only (never on scroll/render), so the
    // ~4-month window is only ever rebuilt when the schedule or timezone
    // actually changes.
    const breakEvents = useMemo(
      () => scheduleToBreakEvents(workingSchedule, timezone),
      [workingSchedule, timezone]
    )

    const views = useMemo(
      () => ({
        ...FC_VIEWS_STATIC,
        timeGridWorkWeek: {
          type: "timeGrid",
          // `duration: { days: 7 }` (the prior value) has no default week
          // alignment, so FullCalendar rolls a 7-day window forward from
          // whatever date is currently focused — viewed on a Tuesday, that's
          // Tue-Mon, and with a working day hidden partway through (e.g.
          // Saturday only) the visible columns read as scrambled: Tue, Wed,
          // Thu, Fri, Sun, Mon. `{ weeks: 1 }` + explicit `dateAlignment`
          // snaps the window to the calendar week (firstDay={0} below, so
          // Sunday-start) so the configured working days always render in
          // real weekday order; `hiddenDays` still hides the same hidden
          // days, so today stays visible whenever it's a working day.
          duration: { weeks: 1 },
          dateAlignment: "week",
          // Driven by the user's own Calendar Settings > Working Schedule
          // (working-schedule.ts) instead of a hardcoded Mon-Fri.
          hiddenDays: workWeekHiddenDays,
          // The viewport itself, not just a background tint — Work Week
          // opens on exactly the configured hours instead of a 24-hour grid
          // marked up with businessHours shading.
          slotMinTime: `${workWeekVisibleRange.start}:00`,
          slotMaxTime: `${workWeekVisibleRange.end}:00`,
          titleFormat: {
            year: "numeric",
            month: "short",
            day: "numeric"
          } as const
        } as const
      }),
      [workWeekHiddenDays, workWeekVisibleRange]
    )

    const [searchQuery, setSearchQuery] = useState("")

    const [nowLine, setNowLine] = useState({
      visible: false,
      top: 0,
      left: 0,
      width: 0,
      axisWidth: 0
    })

    // Client-side only, over whatever the server already returned for the
    // visible range — typing a search must never fire a request per
    // keystroke (the separate autocomplete box in CalendarFiltersSearch
    // still hits the server, since it searches beyond the loaded range).
    const searchedApiEvents = useMemo(
      () => apiEvents.filter((event) => eventMatchesSearch(event, searchQuery)),
      [apiEvents, searchQuery]
    )

    // Unmemoized before, this handed FullCalendar a new `events` array
    // reference on every render — including the now-line's own scroll-driven
    // ones — which is exactly the kind of unrelated-prop churn that made the
    // grid reprocess itself while scrolling (see FC_PLUGINS's comment above).
    const events = useMemo(
      () => transformEventsForCalendar(searchedApiEvents, timezone),
      [searchedApiEvents, timezone]
    )
    const hasAllDayEvents = events.some((event) => event.allDay)
    // The break is schedule-derived, not a real event — merged only into
    // what FullCalendar renders, never into `events` itself, so the empty
    // state, all-day check and event count above stay about real events only.
    const [currentView, setCurrentView] = useState(
      isMobile ? "listWeek" : "timeGridWeek"
    )
    const isAvailabilityView = currentView === "availability"
    // A full-width spanning band reads as a real row (list views) or cell
    // (Month) there, not the "unavailable time" it is in a time grid — kept
    // out of those views entirely rather than relying on their own styling.
    const showBreaks =
      !currentView.startsWith("list") && currentView !== "dayGridMonth"
    const fullCalendarEvents = useMemo(
      () => (showBreaks ? [...events, ...breakEvents] : events),
      [events, breakEvents, showBreaks]
    )

    // Availability's own date, driven directly by every action that changes
    // it rather than read back from FullCalendar afterward. Reading
    // `getAPI()?.getDate()` only reflects a `gotoDate()` call once something
    // re-renders this component, and while Availability is showing, the
    // hidden underlying view's `datesSet` — the thing that normally causes
    // that re-render, via the events refetch — often doesn't fire a change
    // at all: a one-day step easily lands inside the same week/month the
    // hidden view was already showing, so nothing about its own fetch range
    // changes. That silently broke Prev/Next and the mini-month while on
    // Availability. Explicit state sidesteps the question entirely.
    const [availabilityDate, setAvailabilityDate] = useState<Date | null>(null)

    // The date the sidebar's mini-month and "My Schedule" card are about —
    // every non-Availability view's counterpart to `availabilityDate`, and
    // for the identical reason: `getAPI()?.getDate()` only reflects
    // navigation once something re-renders this component, and a date
    // picked from *within* the range a list/agenda view already has loaded
    // (e.g. clicking a day inside the visible Schedule week) changes nothing
    // about that range, so `datesSet` never fires and nothing re-renders.
    // "My Schedule" was reading a stale/wrong date because of exactly this —
    // see docs/kb. Updated at every place the visible date can change:
    // Prev/Next, Today, a mini-month pick, a day-header jump to Day view,
    // FullCalendar's own `datesSet` (Week/Month's built-in navigation, view
    // switches), and the timezone-change effect below.
    const [selectedDate, setSelectedDateState] = useState<Date>(() =>
      dayjs().tz(timezone).toDate()
    )
    // `calendarAPI.getDate()` hands back a new `Date` instance every call,
    // even when the underlying day hasn't moved — a plain `setSelectedDate`
    // therefore re-rendered on every `datesSet`, `Date`-reference-unequal or
    // not. Harmless on its own, but `datesSet` firing as a *result* of that
    // re-render (observed here, not fully root-caused past this point) made
    // it a real infinite loop: "Maximum update depth exceeded", reproduced
    // by the test suite. Comparing by day inside the updater — returning the
    // *same* `prev` reference when nothing actually changed — makes React
    // bail out of the re-render entirely, which breaks the cycle at its
    // weakest link regardless of what upstream keeps re-triggering `datesSet`.
    const setSelectedDate = useCallback((next: Date) => {
      setSelectedDateState((prev) =>
        dayjs(prev).isSame(next, "day") ? prev : next
      )
    }, [])

    const isFiltering =
      !!searchQuery.trim() ||
      filters.type !== null ||
      filters.entity_type !== null ||
      !!filters.participants?.length
    const showEmptyState = isFiltering && events.length === 0

    const handleClearFilters = () => {
      setSearchQuery("")
      onFilterChange({ type: null, entity_type: null, participants: null })
    }

    const now = dayjs().tz(timezone).format()
    // FullCalendar's own `now` prop (a function, distinct from the `now`
    // string above that only feeds `initialDate`) — kept referentially
    // stable so it isn't one more prop that looks "changed" to FullCalendar
    // on a render this has nothing to do with. See FC_PLUGINS's comment.
    const getNow = useCallback(() => dayjs().tz(timezone).format(), [timezone])

    const handleDateClick = useCallback(
      (arg: DateClickArg) => {
        onSetModalData(resolveNewEventSlot(arg.dateStr, timezone, arg.allDay))
      },
      [onSetModalData, timezone]
    )

    const handleEventClick = useCallback(
      (clickInfo: EventClickArg) => {
        const event = clickInfo?.event
        // The break is a background event with no record behind it — normal
        // (non-background) events are all this ever needs to open.
        if (event?.extendedProps?.isBreak) return
        const id = event?.id || event?.extendedProps?.id
        onSelectEvent(id)
      },
      [onSelectEvent]
    )

    // Availability isn't a real FullCalendar view (see the render below), so
    // its own Prev/Next always steps by exactly one day — `.prev()`/`.next()`
    // would otherwise step by whatever duration the hidden underlying view
    // happens to have (a week, a month), which is wrong for a single day.
    const onPrevClick = () => {
      const calendarAPI = getAPI()
      if (!calendarAPI) return

      if (currentView === "availability") {
        const next = dayjs(availabilityDate ?? calendarAPI.getDate())
          .tz(timezone)
          .subtract(1, "day")
          .toDate()
        setAvailabilityDate(next)
        calendarAPI.gotoDate(next)
      } else {
        calendarAPI.prev()
        setSelectedDate(calendarAPI.getDate())
      }
    }

    const onNextClick = () => {
      const calendarAPI = getAPI()
      if (!calendarAPI) return

      if (currentView === "availability") {
        const next = dayjs(availabilityDate ?? calendarAPI.getDate())
          .tz(timezone)
          .add(1, "day")
          .toDate()
        setAvailabilityDate(next)
        calendarAPI.gotoDate(next)
      } else {
        calendarAPI.next()
        setSelectedDate(calendarAPI.getDate())
      }
    }

    const onTodayClick = () => {
      const calendarAPI = getAPI()

      if (calendarAPI) {
        calendarAPI.today()
        calendarAPI.gotoDate(calendarAPI.getDate())
        setSelectedDate(calendarAPI.getDate())
      }
      if (currentView === "availability") {
        setAvailabilityDate(dayjs().tz(timezone).toDate())
      }
    }

    // "availability" has no matching FullCalendar view definition — it's a
    // sibling component swapped in over the same slot (see the render below)
    // while FullCalendar itself stays mounted, unchanged, so its own date and
    // view survive switching away and back.
    const onViewChange = (view: string) => {
      const calendarAPI = getAPI()
      setCurrentView(view)

      if (view === "availability") {
        setAvailabilityDate(
          calendarAPI?.getDate() ?? dayjs().tz(timezone).toDate()
        )
      } else if (calendarAPI) {
        calendarAPI.changeView(view)
      }
    }

    // Mobile and desktop use disjoint view sets (list* vs timeGrid/dayGrid) —
    // if the breakpoint is crossed after mount, the view has to move with it.
    // The initial value is already right (see the `useState` above), so this
    // only needs to act on a later change, not repeat the same `changeView`
    // FullCalendar hasn't even mounted for yet on the first pass.
    const isFirstViewSync = useRef(true)
    useEffect(() => {
      if (isFirstViewSync.current) {
        isFirstViewSync.current = false
        return
      }
      onViewChange(isMobile ? "listWeek" : "timeGridWeek")
    }, [isMobile])

    const handleMoreLinkClick = useCallback(
      (info: MoreLinkArg): any => {
        info.jsEvent.preventDefault()
        onSetMoreInfo(info)
        return () => null
      },
      [onSetMoreInfo]
    )

    // Catches the navigation `selectedDate` above can't observe any other
    // way — Week/Month's own built-in Prev/Next (reached via
    // calendarAPI.prev()/next() in the non-availability branches above,
    // which don't hand back a date synchronously the way gotoDate does) and
    // a view switch landing on a different date range.
    const handleDatesSet = useCallback(
      (arg: DatesSetArg) => {
        onFetchEvents(arg)
        if (!isAvailabilityView) {
          const calendarAPI = getAPI()
          if (calendarAPI) setSelectedDate(calendarAPI.getDate())
        }
      },
      [onFetchEvents, isAvailabilityView]
    )

    const dayHeaderContent = useCallback((args: DayHeaderContentArg) => {
      // Use date directly without time zone conversion
      const date = dayjs.utc(args.date)
      // OR if you need a calendar timezone:
      // const date = dayjs(args.date).tz(args.view.dateEnv.timeZone, true)

      const type = args?.view?.type
      const isToday = args?.isToday

      if (["dayGridMonth"]?.includes(type as string)) {
        return <Text strong>{date.format("ddd")}</Text>
      }

      // Week/Day headers carry an actual date, so jumping to that day's
      // Day view reads as natural; Month's header row is just weekday
      // names shared across the whole grid, so it stays inert.
      const goToDay = () => {
        const calendarAPI = getAPI()
        if (!calendarAPI) return
        calendarAPI.changeView("timeGridDay", args.date)
        setCurrentView("timeGridDay")
        setSelectedDate(args.date)
      }

      return (
        <Flex
          vertical
          className={"px-1 cursor-pointer"}
          gap={2}
          onClick={goToDay}
        >
          <Text
            className={`uppercase tracking-wide text-[11px] ${isToday ? "text-inherit font-bold" : ""}`}
            strong
          >
            {date.format("ddd")}
          </Text>
          <Text
            className={`text-base leading-none ${isToday ? "text-inherit font-bold" : "font-semibold"}`}
          >
            {date.format("MMM D")}
          </Text>
        </Flex>
      )
    }, [])

    const eventDidMount = useCallback((info: EventMountArg) => {
      const event = info.event
      const el = info.el

      // Примусово застосувати кольори з події
      if (event.backgroundColor) {
        el.style.backgroundColor = event.backgroundColor
      }
      if (event.borderColor) {
        el.style.borderColor = event.borderColor
      }
      if (event.textColor) {
        el.style.color = event.textColor
      }

      // Для тексту всередині події
      const titleEl = el.querySelector(".fc-event-title") as HTMLElement

      if (titleEl && event.textColor) {
        titleEl.style.color = event.textColor
      }

      const timeEl = el.querySelector(".fc-event-time") as HTMLElement
      if (timeEl && event.textColor) {
        timeEl.style.color = event.textColor
      }
    }, [])

    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false
        return
      }

      if (timezone) {
        const calendarAPI = getAPI()
        const dateStr = dayjs().tz(timezone).format()

        if (calendarAPI) {
          calendarAPI.gotoDate(dateStr)
          calendarAPI.setOption("now", dateStr)
        }
        // The grid itself just reset to today in the new zone — the
        // sidebar's own idea of "today" has to follow, or a stale
        // selectedDate from the previous zone could point at what is now a
        // different calendar day.
        setSelectedDate(dayjs().tz(timezone).toDate())
      }
    }, [timezone])

    // A meeting opened from a Lead names its date in the URL, so the calendar
    // has to land on that page rather than on today.
    useEffect(() => {
      const target = parseCalendarFocusDate(focusDate)
      if (!target) return
      getAPI()?.gotoDate(target)
    }, [focusDate])

    useEffect(() => {
      const container = containerRef.current
      if (!container) return
      const resizeObserver = new ResizeObserver(() => getAPI()?.updateSize())
      resizeObserver.observe(container)
      return () => resizeObserver.disconnect()
    }, [])

    // FullCalendar's own now-indicator line only ever spans one day column —
    // that's a hard limit of how it lays the line out, not a config option.
    // Rather than reimplement its time-to-pixel math (timezone, slot height,
    // scroll position — all already correct there), this mirrors the
    // position of that line (kept in the DOM but invisible, see calendar.less)
    // onto a second, separately-painted line that spans the full grid width.
    useEffect(() => {
      const updateNowLine = () => {
        const wrap = gridWrapRef.current
        const line = wrap?.querySelector(
          ".fc-timegrid-now-indicator-line"
        ) as HTMLElement | null
        const gridEl = wrap?.querySelector(
          ".fc-timegrid-body"
        ) as HTMLElement | null
        const axisEl = wrap?.querySelector(
          ".fc-timegrid-axis-frame"
        ) as HTMLElement | null

        if (!wrap || !line || !gridEl) {
          setNowLine((prev) =>
            prev.visible ? { ...prev, visible: false } : prev
          )
          return
        }

        // `.fc-timegrid-body` is the full 24-hour scrollable content, not
        // the clipped viewport — its own `.fc-scroller` ancestor is. "now"
        // can fall outside that viewport (e.g. before the 7 AM `scrollTime`
        // anchor, reachable once the Calendar Settings timezone override
        // lets "now" land in the early morning): the real indicator is
        // simply scrolled out of view there, same as any other off-screen
        // content, but this overlay is a sibling FullCalendar's own
        // clipping never reaches, so an unclamped position spills it above
        // the grid into the header/all-day row instead of just hiding it.
        const scroller = gridEl.closest(".fc-scroller") as HTMLElement | null

        if (!scroller) {
          setNowLine((prev) =>
            prev.visible ? { ...prev, visible: false } : prev
          )
          return
        }

        const wrapRect = wrap.getBoundingClientRect()
        const gridRect = gridEl.getBoundingClientRect()
        const scrollerRect = scroller.getBoundingClientRect()
        const axisWidth = axisEl?.getBoundingClientRect()?.width || 0
        const lineTop = line.getBoundingClientRect().top - wrapRect.top
        const visibleTop = scrollerRect.top - wrapRect.top
        const visibleBottom = scrollerRect.bottom - wrapRect.top

        if (lineTop < visibleTop || lineTop > visibleBottom) {
          setNowLine((prev) =>
            prev.visible ? { ...prev, visible: false } : prev
          )
          return
        }

        setNowLine({
          visible: true,
          top: lineTop,
          left: gridRect.left - wrapRect.left,
          width: gridRect.width,
          axisWidth
        })
      }

      updateNowLine()
      // FullCalendar's own DOM can still be mid-mount on the first tick.
      const mountRetry = setTimeout(updateNowLine, 200)

      // A native `scroll` event can fire many times per animation frame;
      // calling `updateNowLine` (a forced-synchronous layout read via
      // `getBoundingClientRect` plus a React state update) on every one of
      // them was the actual cause of the grid visibly shaking while
      // scrolling — not FullCalendar re-measuring anything of its own, but
      // this effect re-measuring and re-rendering far more often than the
      // screen can even show. Coalescing to at most once per frame keeps the
      // line tracking scroll just as closely while cutting the update rate
      // to what the browser can actually paint.
      let scrollRaf = 0
      const onScroll = () => {
        if (scrollRaf) return
        scrollRaf = requestAnimationFrame(() => {
          scrollRaf = 0
          updateNowLine()
        })
      }

      const scroller = gridWrapRef.current
        ?.querySelector(".fc-timegrid-body")
        ?.closest(".fc-scroller")
      scroller?.addEventListener("scroll", onScroll)
      window.addEventListener("resize", updateNowLine)
      // FullCalendar's own indicator re-renders on its own ~minute timer;
      // this just has to catch up promptly after that, not drive the clock.
      const interval = setInterval(updateNowLine, 15000)

      return () => {
        scroller?.removeEventListener("scroll", onScroll)
        window.removeEventListener("resize", updateNowLine)
        clearInterval(interval)
        clearTimeout(mountRetry)
        if (scrollRaf) cancelAnimationFrame(scrollRaf)
      }
    }, [currentView, events.length])

    return (
      // `flex-1 min-h-0` (not `size-full`/`height:100%`): this component is
      // a flex-column sibling of the page's own action bar in CalendarPage,
      // not the sole child of its parent — `height:100%` there would claim
      // the parent's FULL height on top of whatever the action bar already
      // took, overflowing past the shell's bounded content area exactly the
      // way the internal grid below already assumes it can't.
      <Flex
        vertical
        className={`w-full flex-1 min-h-0 ${hasAllDayEvents ? "" : "calendar-no-allday"}`}
        ref={containerRef}
      >
        <Flex vertical gap={12} className={"pb-3"}>
          <CalendarHeader
            createAllowed={createAllowed}
            accountTimezone={accountTimezone}
            onSetModalData={onSetModalData}
          />

          <CalendarFilters
            filters={filters}
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            onFilterChange={onFilterChange}
            onSelectEvent={onSelectEvent}
            onClearFilters={handleClearFilters}
          />
        </Flex>
        <CalendarNavigation
          getAPI={getAPI}
          isMobile={isMobile}
          currentView={currentView}
          timezone={timezone}
          onViewChange={onViewChange}
          onTodayClick={onTodayClick}
          onPrevClick={onPrevClick}
          onNextClick={onNextClick}
        />

        <Flex gap={16} className={"flex-1 min-h-0"}>
          {/* overflow-hidden + rounded-xl mirror .fc's own clipping (calendar.less)
            so the now-line — a sibling of <FullCalendar>, not a descendant —
            is actually clipped to the calendar's rounded border instead of
            painting past it; without this the line's own box had no boundary
            of its own to stop at. */}
          <div
            className={
              "relative flex-1 min-h-0 min-w-0 overflow-hidden rounded-xl"
            }
            ref={gridWrapRef}
          >
            {nowLine.visible && !isAvailabilityView && (
              <>
                <div
                  className={"calendar-now-axis-label"}
                  style={{
                    top: nowLine.top,
                    left: nowLine.left,
                    width: nowLine.axisWidth
                  }}
                >
                  {dayjs().tz(timezone).format("h:mm A")}
                </div>
                <div
                  className={"calendar-now-line"}
                  style={{
                    top: nowLine.top,
                    left: nowLine.left + nowLine.axisWidth,
                    width: nowLine.width - nowLine.axisWidth
                  }}
                />
              </>
            )}

            {showEmptyState && !isAvailabilityView && (
              <Flex
                vertical
                align={"center"}
                justify={"center"}
                gap={4}
                className={
                  "absolute inset-0 z-10 pointer-events-none pt-24 bg-white/70"
                }
              >
                <Text strong className={"text-base"}>
                  No appointments found
                </Text>
                <Text type={"secondary"} className={"text-sm"}>
                  Try changing your filters or search.
                </Text>
                <Button
                  size={"small"}
                  className={"pointer-events-auto mt-1"}
                  onClick={handleClearFilters}
                >
                  Clear filters
                </Button>
              </Flex>
            )}

            {/* Kept mounted (never conditionally removed) so switching to
              Availability and back doesn't lose FullCalendar's own date/view
              state — only visibility toggles. `display:none` looked like the
              obvious way to do that, but FullCalendar can't lay itself out
              (and silently drops `gotoDate`/`prev`/`next` calls) on a
              display:none element, which broke both Availability's own
              Prev/Next and the mini-month's click-to-navigate while
              Availability was showing. `invisible` keeps it laid out and
              functional; `absolute inset-0` keeps that layout from pushing
              Availability's own content down, and it's the last child so
              Availability still paints on top of it. */}
            <div
              className={
                isAvailabilityView
                  ? "invisible absolute inset-0 pointer-events-none"
                  : "size-full"
              }
            >
              <FullCalendar
                views={views}
                // handleWindowResize={false}
                datesSet={handleDatesSet}
                ref={calendarRef}
                plugins={fcPlugins}
                titleFormat={FC_TITLE_FORMAT}
                dayHeaderContent={dayHeaderContent}
                initialView={currentView}
                now={getNow}
                initialDate={now}
                // locale={enGbLocale}
                // locales={[enGbLocale]}
                firstDay={0}
                droppable={false}
                editable={!!editAllowed}
                eventDurationEditable={false}
                //
                selectable={false}
                allDayText={"ALL DAY"}
                nowIndicator
                // FC's own line/arrow render nothing visible (see calendar.less) —
                // the full-width overlay rendered alongside this component's
                // return is what's actually painted, mirroring this line's
                // position. FC still needs `nowIndicator` on to lay that line out
                // at all, which is the only reason it's kept in the DOM.
                nowIndicatorContent={FC_NOW_INDICATOR_CONTENT}
                eventOverlap
                slotEventOverlap
                selectMirror
                dayMaxEvents={2} // Для dayGridMonth
                eventMaxStack={3} // Максимум стеків events в одному слоті
                // A true safety floor for a pathologically short event, not a
                // blanket minimum: this used to equal @slot-height (28px, the
                // *30-minute* slot's own natural height), which meant a 15-minute
                // event was silently stretched to look exactly like a 30-minute
                // one instead of rendering at its own real ~14px — reported live,
                // 20px is just enough for one line of this card's fixed 13px
                // title text (see calendar-event-card-layout.ts's
                // MIN_EVENT_HEIGHT_PX, kept in sync) without clipping it, and
                // sits below every real duration this app books (15 minutes and
                // up), so it only ever engages for something shorter than that.
                eventMinHeight={20}
                weekends
                // Calendar Settings > Working Schedule (working-schedule.ts) — shades
                // non-working days/hours/break automatically via FullCalendar's own
                // fc-non-business styling, no custom rendering needed.
                businessHours={businessHours}
                dragScroll={false}
                events={fullCalendarEvents}
                height="100%"
                timeZone={timezone}
                headerToolbar={false}
                // The default (unbounded) range for every view except Work Week,
                // which overrides both per-view above — see `views.timeGridWorkWeek`.
                slotMinTime="00:00:00"
                slotMaxTime="23:59:00"
                scrollTime={{ hours: 7 }}
                slotLabelFormat={FC_SLOT_LABEL_FORMAT}
                dayHeaderFormat={FC_DAY_HEADER_FORMAT}
                eventTimeFormat={FC_EVENT_TIME_FORMAT}
                eventDidMount={eventDidMount}
                dateClick={createAllowed ? handleDateClick : undefined}
                moreLinkClick={handleMoreLinkClick}
                eventClick={handleEventClick}
                eventContent={CalendarEventCell}
                eventDragStart={startAlignedEventDrag.start}
                eventDragStop={startAlignedEventDrag.stop}
                eventDrop={onDropEvent}
                // eventResize={(info) => console.log("Event resized:", info)}
                // select={(props) => console.log("select:", props)}
              />
            </div>

            {isAvailabilityView && (
              <CalendarAvailability
                date={availabilityDate ?? dayjs().tz(timezone).toDate()}
                timezone={timezone}
                filters={filters}
                viewAllAllowed={viewAllAllowed}
                events={apiEvents}
                userId={userId}
                onSelectEvent={onSelectEvent}
              />
            )}
          </div>

          {!isMobile && (
            <CalendarSidebar
              // The same search-filtered set the main grid renders (`events`
              // below is that, post-transform) — not the raw, unfiltered
              // `apiEvents` this used to pass, which let "My Schedule" disagree
              // with what the user is actually looking at whenever a search
              // was active.
              events={searchedApiEvents}
              timezone={timezone}
              userId={userId}
              filterParticipants={filters.participants}
              currentDate={
                isAvailabilityView
                  ? (availabilityDate ?? dayjs().tz(timezone).toDate())
                  : selectedDate
              }
              onSelectDate={(date) => {
                // FullCalendar's own `gotoDate` already resolves this per view:
                // in Day it lands on that day, in Week/Work Week it moves to
                // the week containing it, in Month it moves to that month —
                // exactly the "click a date -> navigate" behavior asked for,
                // with no per-view branching needed here. Availability isn't a
                // FullCalendar view, so it needs its own state kept in sync
                // directly — see the note by `availabilityDate` above for why
                // relying on `gotoDate` alone silently didn't update it.
                if (isAvailabilityView) setAvailabilityDate(date)
                else setSelectedDate(date)
                getAPI()?.gotoDate(date)
              }}
              onSelectEvent={onSelectEvent}
              onViewSchedule={() => onViewChange("listWeek")}
            />
          )}
        </Flex>
      </Flex>
    )
  }
)

Calendar.displayName = "Calendar"
